import * as React from "react";
import styled, { css } from "styled-components";
import HTMLParser from "html-react-parser";
import DOMPurify from "dompurify";
import { decode } from "html-entities";

import Grid from "src/library/components/Hedron";
import { TextTruncate } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { useCurrentUser } from "src/core/users/hooks";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";

import {
  INotification,
  INotificationRead,
  useNotificationsApi
} from "../api/notifications.api";

interface IProps {
  notification: INotification;
  viewNotification: (id: number) => void;
  dismissedNotificationCallback: (notificationId: INotificationRead) => void;
}

const Notification: React.ReactFCWithChildren<IProps> = ({
  notification, viewNotification, dismissedNotificationCallback
}) => {
  const [ dismissNotificationResponse, dismissNotification ] = useNotificationsApi("DISMISS_NOTIFICATION");
  const [ { currentUser } ] = useCurrentUser();

  const decodedBody = React.useMemo(() => {
    const decodedHTML = decode(notification.body);

    return DOMPurify.sanitize(decodedHTML);
  }, [ notification.body ]);

  const handleDismiss = (notificationId: number) => {
    if (currentUser) {
      dismissNotification({
        userId: currentUser.id,
        notificationId
      });
    }
  };

  React.useEffect(() => {
    if (dismissNotificationResponse.data?.id) {
      dismissedNotificationCallback(dismissNotificationResponse.data);
    }
  }, [ dismissNotificationResponse, dismissedNotificationCallback ]);

  return (
    <NotificationWrapper direction="vertical">
      <div
        className="click-wrapper"
        onClick={() => viewNotification(notification.id)}
      />

      <Icon
        size={10}
        $fillColor="warn"
        className="unread"
        icon={Icons.CIRCLE}
      />

      <TextTruncate weight="bold">
        {notification.title}
      </TextTruncate>

      <TextTruncate>
        {HTMLParser(decodedBody)}
      </TextTruncate>

      {dismissNotificationResponse.pending ? (
        <div className="loading">
          <LoadingSpinner
            type="circular-pulse"
            position="inline"
          />
        </div>
      ) : (
        <Icon
          onClick={() => handleDismiss(notification.id)}
          size={20}
          className="close"
          icon={Icons.CLOSE}
          $fillColor="primaryLight"
        />

      )}
    </NotificationWrapper>
  );
};

export default Notification;

const NotificationWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    background: ${theme.colors.white};
    padding: ${theme.spacing.padding.md} calc(${theme.spacing.padding.md} + 20px);
    margin-bottom: ${theme.spacing.padding.sm};
    position: relative;
    border: ${theme.colors.lightGrey} solid 2px;

    .click-wrapper {
      position: absolute;
      height: 100%;
      width: calc(100% - ${theme.spacing.padding.md} - 20px);
      left: 0;
      top: 0;
      cursor: pointer;
    }

    .unread {
      position: absolute;
      left: ${theme.spacing.padding.sm};
      top: 50%;
      transform: translateY(-50%);
    }

    .close, .loading {
      position: absolute;
      right: ${theme.spacing.padding.sm};
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
    }

    .loading {
      width: 20px;
      height: 20px;
      right: ${theme.spacing.padding.md};
    }
  `}
`;
